import { ContactKey } from '@folio/utils/customerDisplayName';
import { formatDate } from '@folio/lib';
import { offlineAwarePaymentMethodRule } from '@folio/utils/offlineAwarePaymentMethodRule';
import {
  type AfterSubmitFn,
  buildFieldCleanup,
  buildPolicyFieldCleanup,
  isNaturalPersonOrCompanyBeingCreated,
} from '@folio/modules';
import { DocumentsListsPolicyModule, InvoicesScheduleModule } from '@korint/folio';
import {
  type BrokerageFeeOnPolicy,
  clientWithFork,
  derogationControllerCheckDerogationRequirement,
  derogationControllerCreateDerogation,
  getKorintErrorCode,
  getNewBranchIdAfterFork,
  type Policy,
  policyControllerEditPolicy,
  policyControllerPushPolicyEvent,
  toKeyValueFields,
} from 'korint-sdk';
import type {
  ConditionalVisibility,
  Field,
  FieldShortcut,
  FormSections,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import {
  Building2,
  Cake,
  Car,
  CarIcon,
  CarTaxiFront,
  Check,
  Globe,
  Mail,
  MapPin,
  Phone,
  User,
  X,
} from 'lucide-react';
import {
  ImaAssistanceComponentIds,
  axeceDriverContactFields,
  axeceDriverDeclarationFields,
  axeceDriverLicenseFields,
  getFieldsByDriver,
  secondDriverOption,
} from '../configs/constants';
import { retailSubscriptionFeeSection } from '../configs/retailSubscriptionFeeSection';
import { ClaimsStep } from '../custom-pages/ClaimsStep';
import { DriveQuantComparison } from '../custom-pages/DriveQuantComparison';
import { DrivequantInactiveUserBanner } from '../custom-pages/DrivequantInactiveUserBanner';
import { PolicyholderConfirmationStep } from '../custom-pages/PolicyholderConfirmationStep';
import { PolicyholderDeclarationsStep } from '../custom-pages/PolicyholderDeclarationsStep';
import { PolicyholderSummaryStep } from '../custom-pages/PolicyholderSummaryStep';
import { PolicyWakamErrorBanner } from '../custom-pages/PolicyWakamErrorBanner';
import { QuoteWakamError } from '../custom-pages/QuoteWakamError';

const stopReasonsMapping = {
  VOID: 'Nullité de contrat',
  RISK_AGGRAVATION: 'Aggravation du risque',
  AMICABLE: 'Amiable',
  ACTIVITY_CESSATION: "Cessation d'activité",
  COMPANY_CHANGE: 'Changement de compagnie',
  DEATH: 'Décès',
  MISSING_DOCUMENTS: 'Documents manquants',
  DESTRUCTION: 'Destruction',
  MAIN_DUE_DATE: 'Échéance principale',
  FALSE_DECLARATION: 'Fausse déclaration',
  CHATEL_LAW: 'Loi Chatel',
  HAMON_LAW: 'Loi Hamon',
  NON_PAYMENT: 'Non paiement',
  RADIATION: 'Radiation',
  CONTRACT_REPLACEMENT: 'Remplacement de contrat',
  COMPANY_TERMINATION: 'Résiliation compagnie',
  VEHICLE_RETURN: 'Restitution véhicule',
  LICENSE_WITHDRAWAL: 'Retrait de permis',
  NO_EFFECT: 'Sans effet',
  CLAIMS: 'Sinistralité',
  SALE: 'Vente',
  THEFT: 'Vol',
  OTHER: 'Autre',
  REFUSAL_TO_INCREASE_PREMIUM: "Refus d'augmentation de prime",
  WAIVER: 'Renonciation (14j)',
};

const policyHolderInvitationRole = ['DRIVER'];

const showWhenLegalPerson = {
  fieldName: 'legalNaturalPerson',
  condition: 'equals',
  value: 'LEGAL_PERSON',
} as const satisfies ConditionalVisibility;

const showWhenCompanyExists = {
  fieldName: 'isCompanyBeingCreated',
  condition: 'equals',
  value: false,
} as const satisfies ConditionalVisibility;

const showWhenCompanyBeingCreated = {
  fieldName: 'isCompanyBeingCreated',
  condition: 'equals',
  value: true,
} as const satisfies ConditionalVisibility;

const customerFields = [
  {
    name: 'legalNaturalPerson',
    options: ['NATURAL_PERSON', 'LEGAL_PERSON'],
    type: 'radio',
    display: 'cards',
    icons: { NATURAL_PERSON: Car, LEGAL_PERSON: Building2 },
  },
  {
    name: 'isCompanyBeingCreated',
    required: false,
    type: 'checkbox',
    showWhen: [showWhenLegalPerson],
  },
  {
    name: 'companyCustomName',
    showWhen: [showWhenCompanyBeingCreated],
  },
  {
    name: 'companySiret',
    required: true,
    type: 'company',
    displayCompanyInformation: true,
    apiDataAccessor: apiData =>
      apiData.customFields?.companySiret
        ? {
            siret: apiData?.customFields?.companySiret,
            name: apiData?.customFields?.companyName,
            location: {
              postCode: apiData?.customFields?.companyHeadquarterPostcode,
            },
            nafLabel: apiData?.customFields?.companyNafLabel,
          }
        : null,
    formDataAccessor: 'companySiret.siret',
    showWhen: [showWhenLegalPerson, showWhenCompanyExists],
  },
] as const satisfies Array<FieldShortcut>;

const customerSections = [{ fields: customerFields }] as const satisfies FormSectionsWithShortcuts;

const cleanCompanyFields = buildFieldCleanup(isNaturalPersonOrCompanyBeingCreated, [
  'companyName',
  'companyCustomName',
  'companySiren',
  'companySiret',
  'companyNafCode',
  'companyNafLabel',
  'companyCreationDate',
  'companyHeadquarterAddress',
  'companyHeadquarterPostcode',
  'companyHeadquarterCity',
  'companyHeadquarterCountry',
  'companyLegalStatus1',
  'companyLegalStatus2',
  'companyLegalStatus3',
]);

const usageSections = [
  {
    fields: [
      {
        name: 'usage',
        type: 'radio',
        display: 'cards',
        icons: { VEHICLE_FOR_HIRE: Car, TAXI_AND_LIGHT_SANITARY_VEHICLE: CarTaxiFront },
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const signatoryFields = [
  { name: 'contactCivility', colSpan: 4 },
  { name: 'contactLastName', icon: User, colSpan: 4 },
  { name: 'contactFirstName', icon: User, colSpan: 4 },
  {
    name: 'contactBirthDate',
    max: 'startOfDay-23y',
    icon: Cake,
    colSpan: 4,
  },
  {
    name: 'contactBirthCity',
    type: 'city',
    addressGroup: 'contactBirth',
    icon: MapPin,
    colSpan: 4,
  },
  {
    name: 'contactBirthCountry',
    type: 'country',
    addressGroup: 'contactBirth',
    icon: Globe,
    colSpan: 4,
  },
  { name: 'contactEmail', icon: Mail, colSpan: 6 },
  { name: 'contactPhoneNumber', icon: Phone, colSpan: 6 },
  { name: 'isMainDriver', display: 'cards', icons: { true: Check, false: X } },
] as const satisfies Array<Field | FieldShortcut>;

const signatorySections = [
  { fields: signatoryFields },
] as const satisfies FormSectionsWithShortcuts;

const copyMainDriverToPolicy: AfterSubmitFn = async (data, params) => {
  const isMainDriver = data.isMainDriver === true || data.isMainDriver === 'true';
  if (!isMainDriver || !params.isDirty) return;

  const response = await policyControllerEditPolicy(
    params.policyId,
    {
      customFields: toKeyValueFields({
        driverCivility1: data.contactCivility as string,
        driverLastName1: data.contactLastName as string,
        driverFirstName1: data.contactFirstName as string,
        driverBirthDate1: data.contactBirthDate as string,
        driverBirthCity1: data.contactBirthCity as string,
        driverBirthCountry1: data.contactBirthCountry as string,
        driverEmail1: data.contactEmail as string,
        driverPhoneNumber1: data.contactPhoneNumber as string,
      }),
    },
    { branchId: params.branchId },
    { client: clientWithFork },
  );
  return { branchId: getNewBranchIdAfterFork(response) };
};

const driver1ContactFields = getFieldsByDriver(axeceDriverContactFields, '1');
const driver1LicenseFields = getFieldsByDriver(axeceDriverLicenseFields, '1');
const driver1DeclarationFields = getFieldsByDriver(axeceDriverDeclarationFields, '1');

const driverSections: FormSectionsWithShortcuts = [
  { fields: driver1ContactFields },
  { fields: driver1LicenseFields },
  { fields: driver1DeclarationFields },
];

const mtaDriver1LicenseFields = driver1LicenseFields.map(field => {
  if (typeof field === 'string') {
    return { name: `${field}1` };
  }
  return field.name === 'CRM1' ? { ...field, disabled: true, required: false } : field;
});

const mtaDriverSections: FormSectionsWithShortcuts = [
  { fields: driver1ContactFields },
  { fields: mtaDriver1LicenseFields },
  { fields: [{ name: 'CRM', required: true } satisfies FieldShortcut] },
  { fields: driver1DeclarationFields },
];

const driver2ContactFields = getFieldsByDriver(axeceDriverContactFields, '2');
const driver2LicenseFields = getFieldsByDriver(axeceDriverLicenseFields, '2');
const driver2DeclarationFields = getFieldsByDriver(axeceDriverDeclarationFields, '2');

const crm2Field = driver2LicenseFields.find(
  field => typeof field !== 'string' && field.name === 'CRM2',
);
const driver2LicenseFieldsWithoutCrm2 = driver2LicenseFields.filter(
  field => typeof field === 'string' || field.name !== 'CRM2',
);

const secondDriverShowWhen = [
  { fieldName: secondDriverOption.name, condition: 'equals' as const, value: true },
];

// When the second driver is removed, the funnel only submits secondDriverOption=false;
// null out the orphaned driver-2 fields so they don't persist on the policy (ENG-3823).
const driver2Keys = [
  ...driver2ContactFields,
  ...driver2LicenseFields,
  ...driver2DeclarationFields,
].map(field => (typeof field === 'string' ? field : field.name));

const secondDriverRemoved = (data: Record<string, unknown>) =>
  data.secondDriverOption !== true && data.secondDriverOption !== 'true';

const cleanSecondDriverFields = buildPolicyFieldCleanup(secondDriverRemoved, driver2Keys);

const secondaryDriverSections: FormSectionsWithShortcuts = [
  { fields: [secondDriverOption] },
  { fields: driver2ContactFields, showWhen: secondDriverShowWhen },
  { fields: driver2LicenseFields, showWhen: secondDriverShowWhen },
  { fields: driver2DeclarationFields, showWhen: secondDriverShowWhen },
];

const mtaSecondaryDriverSections: FormSectionsWithShortcuts = [
  { fields: [secondDriverOption] },
  { fields: driver2ContactFields, showWhen: secondDriverShowWhen },
  { fields: driver2LicenseFieldsWithoutCrm2, showWhen: secondDriverShowWhen },
  ...(crm2Field
    ? [
        {
          description: 'À renseigner si le second conducteur est ajouté lors de cet avenant.',
          fields: [crm2Field],
          showWhen: secondDriverShowWhen,
        },
      ]
    : []),
  { fields: driver2DeclarationFields, showWhen: secondDriverShowWhen },
];

const vehicleFields: Array<FieldShortcut> = [
  {
    name: 'vehicleInput',
    type: 'vehicleInput',
    tabs: ['plate', 'brandModel', 'manual'],
    manualFlagField: 'isManualVehicle',
    manualTabFirmTypes: ['WHOLESALE'],
    identityFields: [
      'registrationNumber',
      'brand',
      'model',
      'version',
      'firstCirculationDate',
      'fiscalPower',
      'numberOfSeats',
    ],
  },
  {
    name: 'vehicleNewValue',
    type: 'number',
    required: true,
    isCustomField: true,
    showWhen: [{ fieldName: 'isManualVehicle', condition: 'equals', value: true }],
  },
  {
    name: 'energy',
    type: 'select',
    options: [
      { value: 'ES', label: 'Essence' },
      { value: 'GO', label: 'Gazole' },
      { value: 'EL', label: 'Électrique' },
      { value: 'EE', label: 'Hybride rechargeable' },
      { value: 'EH', label: 'Hybride non rechargeable' },
    ],
    required: true,
    isCustomField: true,
    showWhen: [{ fieldName: 'isManualVehicle', condition: 'equals', value: true }],
  },
  {
    name: 'marketValue',
    type: 'number',
    required: true,
    isCustomField: true,
    showWhen: [
      { fieldName: 'vehicleNewValue', condition: 'greaterThanOrEquals', value: 150_000 },
      { fieldName: 'firstCirculationDate', condition: 'dateOlderThanYears', value: 1 },
    ],
  },
  {
    name: 'sraGroup',
    type: 'text',
    required: true,
    isCustomField: true,
    firmTypes: ['WHOLESALE'],
    showWhen: [{ fieldName: 'isManualVehicle', condition: 'equals', value: true }],
  },
  {
    name: 'sraClass',
    type: 'text',
    required: true,
    isCustomField: true,
    firmTypes: ['WHOLESALE'],
    showWhen: [{ fieldName: 'isManualVehicle', condition: 'equals', value: true }],
  },
  'remainingLeasingDuration',
  { name: 'noPublicGoodsTransport', type: 'checkbox', required: true },
];

const quoteAssetSections: FormSections = [
  {
    title: "Date de prise d'effet du véhicule",
    fields: [{ name: 'startedAt', type: 'datetime', required: true }],
  },
];

const quotePolicySections: FormSections = [
  {
    title: 'Commission de distribution',
    fields: [
      {
        name: 'commissionRate',
        type: 'number',
        required: true,
        integer: true,
        min: 0,
        max: 10,
        apiDataAccessor: (policy: Policy) =>
          policy.brokerageFees?.find(
            ({ brokerageFirmId }: BrokerageFeeOnPolicy) => brokerageFirmId === policy.brokerageFirm,
          )?.fee,
        formDataAccessor: formData => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.commissionRate ?? 0),
            },
          ],
        }),
      },
      {
        name: 'commercialRate',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        max: 10,
        firmTypes: ['WHOLESALE'],
        apiDataAccessor: (policy: Policy) => {
          const rate = Number.parseFloat(String(policy.customFields?.commercialRate ?? ''));
          return Number.isNaN(rate) ? undefined : rate;
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.commercialRate == null || formData.commercialRate === ''
            ? undefined
            : Number(formData.commercialRate),
      },
    ],
  },
];

// commercialRate is FORBIDDEN on MTA: show any stored rate, without the new-business range
const mtaQuotePolicySections: FormSections = quotePolicySections.map(section => ({
  ...section,
  fields: section.fields.map(field =>
    field.name === 'commercialRate'
      ? { ...field, min: undefined, max: undefined, description: '', disabled: true }
      : field,
  ),
}));

// managementFeeTaxesIncluded is required for QUOTE and stored in cents
const quoteAdditionalComponentsSections: FormSections = [
  {
    fields: [
      {
        name: 'managementFeeTaxesIncluded',
        isCustomField: true,
        type: 'number',
        required: true,
        min: 0,
        defaultValue: 0,
        apiDataAccessor: (policy: Policy) => {
          const storedInCents = policy.customFields?.managementFeeTaxesIncluded as
            | number
            | undefined;
          return storedInCents === undefined ? undefined : storedInCents / 100;
        },
        formDataAccessor: formData =>
          Math.round(Number(formData.managementFeeTaxesIncluded ?? 0) * 100),
      },
    ],
  },
  retailSubscriptionFeeSection,
];

const requestMtaConfirmDerogation = async ({ branchId }: { branchId: string }) => {
  const { needsDerogation } = await derogationControllerCheckDerogationRequirement(
    branchId,
    {},
    { action: 'CONFIRM' },
  );

  if (!needsDerogation) {
    return;
  }

  try {
    await derogationControllerCreateDerogation(branchId, { action: 'CONFIRM' });
  } catch (error) {
    if (getKorintErrorCode(error) !== 'DEROGATION_ALREADY_EXISTS') {
      throw error;
    }
  }
};

export const AxeceVtcConfig = {
  display: {
    category: 'automobile',
    icon: CarIcon,
    badges: [],
  },
  enableAssetComparison: true,
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  productCustomPath: '/vtc',
  policyWarningBanners: [DrivequantInactiveUserBanner, PolicyWakamErrorBanner],
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  discoveryFunnelSteps: {
    steps: [
      { type: 'documentUploadFunnel', path: 'documents-upload', context: 'policy' },
      {
        type: 'customerFunnel',
        path: 'customer',
        sections: customerSections,
        afterSubmit: cleanCompanyFields,
      },
      { type: 'policyFunnel', path: 'policy', sections: usageSections },
      {
        type: 'customerFunnel',
        path: 'signatory',
        sections: signatorySections,
        afterSubmit: copyMainDriverToPolicy,
      },
      { type: 'policyFunnel', path: 'driver', sections: driverSections },
      {
        type: 'policyFunnel',
        path: 'secondary-driver',
        sections: secondaryDriverSections,
        afterSubmit: cleanSecondDriverFields,
      },
      // Every claim must carry its responsibility before Next, which `module` cannot express.
      { type: 'custom', path: 'claims', component: <ClaimsStep /> },
      {
        type: 'assetFunnel',
        path: 'vehicle',
        fields: vehicleFields,
        minAssets: 1,
        maxAssets: 1,
      },
      { type: 'billingSelectionFunnel', path: 'billing-frequency' },
      {
        type: 'quoteFunnel',
        path: 'quote',
        additionalComponent: <QuoteWakamError />,
        assetSections: quoteAssetSections,
        policySections: quotePolicySections,
        additionalQuoteComponentsSections: quoteAdditionalComponentsSections,
        additionalComponentAfterOptions: <DriveQuantComparison />,
      },
      { type: 'module', path: 'invoicing-schedule', module: <InvoicesScheduleModule /> },
      {
        type: 'funnelEnd',
        path: 'end',
        id: 'new-business-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          showDocumentsGeneratedAtQuote: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'AXECE_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  // `mta-end` rather than `end`: uniq gives every funnel of a product one i18n namespace.
  mtaFunnelSteps: {
    steps: [
      { type: 'newMTACreation', path: 'new' },
      {
        type: 'customerFunnel',
        path: 'customer',
        sections: customerSections,
        afterSubmit: cleanCompanyFields,
      },
      { type: 'policyFunnel', path: 'policy', sections: usageSections },
      {
        type: 'customerFunnel',
        path: 'signatory',
        sections: signatorySections,
        afterSubmit: copyMainDriverToPolicy,
      },
      { type: 'policyFunnel', path: 'driver', sections: mtaDriverSections },
      {
        type: 'policyFunnel',
        path: 'secondary-driver',
        sections: mtaSecondaryDriverSections,
        afterSubmit: cleanSecondDriverFields,
      },
      { type: 'custom', path: 'claims', component: <ClaimsStep mode="add-only" /> },
      {
        type: 'assetFunnel',
        path: 'vehicle',
        fields: vehicleFields,
        minAssets: 1,
        maxAssets: 1,
      },
      {
        type: 'quoteFunnel',
        path: 'quote',
        additionalComponent: <QuoteWakamError />,
        assetSections: quoteAssetSections,
        policySections: mtaQuotePolicySections,
        additionalQuoteComponentsSections: quoteAdditionalComponentsSections,
        additionalComponentAfterOptions: <DriveQuantComparison />,
      },
      { type: 'module', path: 'invoicing-schedule', module: <InvoicesScheduleModule /> },
      {
        type: 'funnelEnd',
        path: 'mta-end',
        id: 'mta-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          showDocumentsGeneratedAtQuote: true,
        },
        callback: { callToActionCallback: requestMtaConfirmDerogation },
      },
    ],
  },
  // `confirmation` rather than `end`, for the same shared-namespace reason as `mta-end`.
  policyholderDiscoveryFunnelSteps: {
    steps: [
      { type: 'documentUploadFunnel', path: 'documents-upload', context: 'policy' },
      { type: 'custom', path: 'summary', component: <PolicyholderSummaryStep /> },
      // Declarations that gate Next and persist nothing — no funnel type models that.
      { type: 'custom', path: 'declarations', component: <PolicyholderDeclarationsStep /> },
      { type: 'paymentMethodFunnel', path: 'payment-method' },
      {
        type: 'module',
        path: 'documents',
        module: <DocumentsListsPolicyModule displayContext="policyholder" />,
      },
      { type: 'intermediarySignatureFunnel', path: 'signature', displayContext: 'policyholder' },
      { type: 'custom', path: 'confirmation', component: <PolicyholderConfirmationStep /> },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      {
        key: 'companySiret',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
      },
      {
        key: 'companyName',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
        contactKey: ContactKey.COMPANY_NAME,
      },
      {
        key: 'companyCustomName',
        type: 'text',
        hideIfEmpty: true,
        contactKey: ContactKey.TEMPORARY_CUSTOMER_NAME,
      },
      {
        key: 'companyHeadquarterAddress',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
      },
      {
        key: 'companyHeadquarterPostcode',
        type: 'postcode',
        countries: ['FR'],
        hideIfEmpty: true,
        forceReadonly: true,
      },
      {
        key: 'companyHeadquarterCity',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
      },
      {
        key: 'companySiren',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
      },
      {
        key: 'companyNafCode',
        type: 'text',
        hideIfEmpty: true,
        forceReadonly: true,
      },
      'contactCivility',
      {
        key: 'contactLastName',
        type: 'text',
        contactKey: ContactKey.LAST_NAME,
      },
      {
        key: 'contactFirstName',
        type: 'text',
        contactKey: ContactKey.FIRST_NAME,
      },
      {
        key: 'contactBirthDate',
        type: 'date',
      },
      {
        key: 'contactBirthCity',
        type: 'text',
      },
      {
        key: 'contactBirthCountry',
        type: 'text',
      },
      {
        key: 'contactEmail',
        type: 'email',
      },
      {
        key: 'contactPhoneNumber',
        type: 'tel',
      },
    ],
  },
  policyInformationCard: {
    title: 'Informations du/des conducteurs',
    fieldsConfig: [
      {
        key: 'usage',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (value === 'VEHICLE_FOR_HIRE') return 'VTC';
          if (value === 'TAXI_AND_LIGHT_SANITARY_VEHICLE') return 'Taxi';
          return undefined;
        },
      },
      {
        key: 'driverChosen',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) =>
          typeof value === 'number' && value === 1
            ? 'Conducteur principal'
            : 'Conducteur secondaire',
      },
      {
        key: 'CRM',
        type: 'number',
      },
      'driverCivility1',
      {
        key: 'driverLastName1',
        type: 'text',
      },
      {
        key: 'driverFirstName1',
        type: 'text',
      },
      {
        key: 'driverBirthDate1',
        type: 'date',
      },
      {
        key: 'driverBirthCity1',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'driverBirthCountry1',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'parkingPostcode1',
        type: 'text',
      },
      {
        key: 'parkingCity1',
        type: 'text',
      },
      {
        key: 'driverEmail1',
        type: 'email',
      },
      {
        key: 'driverPhoneNumber1',
        type: 'tel',
      },
      {
        key: 'licenseDate1',
        type: 'date',
        formatValue: (value: string | number | boolean | undefined) => {
          if (!value || typeof value === 'boolean') return undefined;
          const date = new Date(value);
          return !Number.isNaN(date.getTime()) ? formatDate(date) : undefined;
        },
      },
      {
        key: 'background1',
        type: 'text',
      },
      {
        key: 'professionalExperience1',
        type: 'text',
      },
      {
        key: 'CRM1',
        type: 'number',
      },
      {
        key: 'numberOfYearBonus501',
        type: 'number',
      },
      {
        key: 'driverCivility2',
        hideIfEmpty: true,
      },
      {
        key: 'driverLastName2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'driverFirstName2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'driverBirthDate2',
        type: 'date',
        hideIfEmpty: true,
      },
      {
        key: 'driverBirthCity2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'driverBirthCountry2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'parkingPostcode2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'parkingCity2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'driverEmail2',
        type: 'email',
        hideIfEmpty: true,
      },
      {
        key: 'driverPhoneNumber2',
        type: 'tel',
        hideIfEmpty: true,
      },
      {
        key: 'licenseDate2',
        type: 'date',
        hideIfEmpty: true,
      },
      {
        key: 'background2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'professionalExperience2',
        type: 'text',
        hideIfEmpty: true,
      },
      {
        key: 'CRM2',
        type: 'number',
        hideIfEmpty: true,
      },
      {
        key: 'numberOfYearBonus502',
        type: 'number',
        hideIfEmpty: true,
      },
    ],
  },
  assetInformations: {
    fieldsConfig: [
      {
        key: 'registrationNumber',
        type: 'text',
      },
      {
        key: 'brand',
        type: 'text',
      },
      {
        key: 'model',
        type: 'text',
      },
      {
        key: 'version',
        type: 'text',
      },
      {
        key: 'firstCirculationDate',
        type: 'date',
        hideIfEmpty: true,
      },
      {
        key: 'fiscalPower',
        type: 'number',
        hideIfEmpty: true,
      },
      {
        key: 'remainingLeasingDuration',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) =>
          value === 'NO_LEASING' ? 'Comptant' : 'Leasing',
      },
    ],
  },
  priceBreakdownConfig: {
    groups: [
      {
        lines: [
          { key: 'PREMIUM_PRE_TAXES' },
          { key: 'PREMIUM_TAXES' },
          { key: 'TOTAL_PREMIUM', variant: 'bold' },
          { key: 'TOTAL_PREMIUM_RETAIL', variant: 'bold' },
          { key: 'MANAGEMENT_FEE' },
          { key: 'MANAGEMENT_FEE_RETAIL' },
          { key: 'SUBSCRIPTION_FEE', tooltip: true },
          { key: 'SUBSCRIPTION_FEE_RETAIL', tooltip: true },
        ],
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: true,
    claimsReview: {
      attachedTo: 'policy',
      sections: [
        { id: 'driver1', claimsMetadata: { driver: '1' } },
        {
          id: 'driver2',
          claimsMetadata: { driver: '2' },
          showWhen: [
            { apiProperty: 'customFields.secondDriverOption', condition: 'equals', value: true },
          ],
        },
      ],
    },
  },
  delegatedQuoteModuleConfig: {
    lockedPeril: { id: 'roadside-assistance', featureFlag: 'ops_axece_vtc_assistance_suspended' },
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/vtc/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/vtc/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {
      'vehicle-modification': ['AMV'],
      'glass-breakage': ['BDG'],
      luggage: ['BGG'],
      'technological-disasters': ['CATECH'],
      'natural-disasters': ['CATNAT'],
      'all-accident-damage': ['CATNAT'],
      'criminal-defense-and-appeal': ['DRX'],
      protests: ['EMP'],
      'personal-driver-equipment': ['EOPC'],
      'professional-equipment': ['EQPRO'],
      'weather-events': ['EVCLM'],
      fire: ['INC'],
      'replacement-value-compensation': ['IVAN'],
      'replacement-vehicle-compensation': ['IVR'],
      'legal-protection': ['PJ'],
      'driver-protection': ['PROPILE'],
      'professional-indemnity': ['RCPRO'],
      'public-liability': ['RCX'],
      theft: ['VLX'],
      'roadside-assistance': ImaAssistanceComponentIds,
    },
    isExternalQuoteEnabled: true,
    isChosenTierDisplayed: true,
  },
  policyholderClaimsDeclarationModuleConfig: {
    title: 'Déclaration de sinistre',
    description:
      'Vous pouvez déclarer votre sinistre en ligne en cliquant sur le bouton ci-dessous',
    cta: {
      label: 'Déclarer un sinistre',
      url: 'https://plugnplay.aikan.io/axece/declaration-sinistre/auto',
    },
  },
  claims: {
    claimsCategories: [
      {
        category: 'BODILY_HARM',
        label: 'Corporel',
        hasResponsibility: true,
      },
      {
        category: 'MATERIAL_DAMAGE',
        label: 'Matériel',
        hasResponsibility: true,
      },
      {
        category: 'THEFT',
        label: 'Vol',
      },
      {
        category: 'FIRE',
        label: 'Incendie',
      },
      {
        category: 'GLASS_BREAKAGE',
        label: 'Bris de glace',
      },
      {
        category: 'PARKED_DAMAGE',
        label: 'En stationnement',
      },
    ],
  },
  stopReasonsMapping,
  finalizeNewBusinessModuleConfig: {
    rules: [
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: offlineAwarePaymentMethodRule({ requiredType: 'sepa_debit' }),
      },
    ],
  },
} as const satisfies KorintModulesProductsConfig;
